/**
 * place-primitive - constructs basic geometry actors (cubes, spheres, etc.). Accepts
 * primitive descriptors, generates Three.js geometries and materials, applies transforms,
 * and adds actors to the world.
 */
import * as THREE from 'three';

import { Actor } from '../actors/Actor.js';
import { MeshComponent } from '../components/visual/MeshComponent.js';
import { MaterialUtils } from '../materials.js';

import type { ActorMiscInfo, Transform } from './common.js';
import type { ActorOptions } from '../actors/Actor.js';
import type { MeshComponentOptions } from '../components/visual/MeshComponent.js';
import type { World } from '../game/World.js';


export enum PrimitiveType {
  Cube = 'cube',
  Cone = 'cone',
  Cylinder = 'cylinder',
  Sphere = 'sphere',
  Torus = 'torus',
  Capsule = 'capsule'
}

export interface PrimitiveArgs {
  type: string;
  transform?: Transform;
  color?: THREE.ColorRepresentation;
}

export interface PrimitiveActorArgs {
  primitives: PrimitiveArgs[];
  transform?: Transform;
  miscInfo?: ActorMiscInfo;
}

export function placePrimitives(args: {
  world: World;
  primitiveActors: PrimitiveActorArgs[];
}): Actor[] {
  const actors: Actor[] = [];

  for (const primitiveActor of args.primitiveActors) {
    const primitiveComponents: MeshComponent[] = [];
    for (const primitive of primitiveActor.primitives) {
      let geometry: THREE.BufferGeometry;
      const primitiveType = primitive.type as PrimitiveType;
      switch (primitiveType) {
        case PrimitiveType.Cube:
          geometry = new THREE.BoxGeometry(1, 1, 1);
          break;
        case PrimitiveType.Cone:
          geometry = new THREE.ConeGeometry(0.5, 1, 32);
          break;
        case PrimitiveType.Cylinder:
          geometry = new THREE.CylinderGeometry(0.5, 0.5, 1, 32);
          break;
        case PrimitiveType.Sphere:
          geometry = new THREE.SphereGeometry(0.5, 32, 32);
          break;
        case PrimitiveType.Torus:
          geometry = new THREE.TorusGeometry(0.5, 0.2, 16, 100);
          break;
        case PrimitiveType.Capsule:
          geometry = new THREE.CapsuleGeometry(0.5, 1, 4, 8);
          break;
        default:
          throw new Error(`Unsupported primitive type: ${primitive.type}`);
      }

      let material = MaterialUtils.DefaultMaterial;
      if (primitive.color != null) {
        material = new THREE.MeshStandardMaterial({ color: primitive.color });
      }

      const meshOptions: MeshComponentOptions = {
        geometry,
        material,
      };

      const transform = primitive.transform;
      if (transform) {
        transform.position && (meshOptions.position = transform.position);
        transform.rotation && (meshOptions.rotation = transform.rotation);
        transform.scale && (meshOptions.scale = transform.scale);
      }

      const mesh = MeshComponent.create(meshOptions);
      primitiveComponents.push(mesh);
    }

    const isSinglePrimitive = primitiveActor.primitives.length === 1 && !primitiveActor.primitives[0].transform;
    const actorOptions: ActorOptions = {};
    const actorTransform = primitiveActor.transform;
    if (actorTransform) {
      if (actorTransform.position) actorOptions.position = actorTransform.position;
      if (actorTransform.rotation) actorOptions.rotation = actorTransform.rotation;
      if (actorTransform.scale) actorOptions.scale = actorTransform.scale;
    }

    const actor = Actor.create(actorOptions);
    if (isSinglePrimitive) {
      // Copy actor-level transform from the default root onto the mesh root.
      actor.setRootComponent(primitiveComponents[0], true);
    } else {
      actor.addComponents(...primitiveComponents);
    };


    if (primitiveActor.miscInfo) {
      const { name, ...rest } = primitiveActor.miscInfo;
      if (name) actor.setName(name);
      Object.assign(actor.editorData, rest);
    }

    args.world.addActors(actor);
    actors.push(actor);
  }

  return actors;
}
